/**
 * Block 12 offline-first sync queue orchestrator.
 *
 * Bridges IDB (Phase A3) ↔ Service Worker Background Sync (Phase A4).
 *
 * Responsibilities:
 *   - getPendingCount() — сумма не-synced записей в projects/quotes/pendingMedia
 *   - bulkPromotePendingToSynced() — Update IDB syncStatus 'pending' → 'synced'
 *     после того как SW BackgroundSync queue завершил replay
 *   - triggerManualSync() — UI retry button (dispatches window.online event)
 *
 * NB: Workbox по умолчанию НЕ posts messages при sync success. MVP подход —
 * periodic poll каждые 30s + при window.online event (forceRefresh + delay).
 * Phase 9+ — custom SW source с explicit URL→IDB id mapping для precise updates.
 *
 * References:
 *   - ADR-0019 §Q11.4 (Background Sync orchestration)
 *   - ADR-0019 §Q11.6 (useOnlineStatus hook)
 *   - https://developer.chrome.com/docs/workbox/modules/workbox-background-sync/
 */

import { listAllPendingMedia } from '../idb/pendingMedia';
import {
  countProjectsBySyncStatus,
  listProjectsBySyncStatus,
  updateProjectSyncStatus,
} from '../idb/projects';
import {
  countQuotesBySyncStatus,
  listQuotesBySyncStatus,
  updateQuoteSyncStatus,
} from '../idb/quotes';

export interface PendingCountBreakdown {
  projects: number;
  quotes: number;
  pendingMedia: number;
  total: number;
}

/**
 * Read IDB across projects/quotes/pendingMedia, return count of non-synced records.
 *
 * Used by:
 *   - useOnlineStatus() hook (initial fetch + periodic refresh)
 *   - OnlineIndicator badge count
 */
export async function getPendingCount(): Promise<PendingCountBreakdown> {
  if (typeof indexedDB === 'undefined') {
    return { projects: 0, quotes: 0, pendingMedia: 0, total: 0 };
  }
  const [localProjects, pendingProjects, localQuotes, pendingQuotes, allMedia] = await Promise.all([
    countProjectsBySyncStatus('local'),
    countProjectsBySyncStatus('pending'),
    countQuotesBySyncStatus('local'),
    countQuotesBySyncStatus('pending'),
    listAllPendingMedia(),
  ]);
  const projects = localProjects + pendingProjects;
  const quotes = localQuotes + pendingQuotes;
  const pendingMedia = allMedia.length;
  return { projects, quotes, pendingMedia, total: projects + quotes + pendingMedia };
}

/**
 * Bulk promote 'pending' projects/quotes → 'synced'.
 *
 * Called когда SW Background Sync queue завершил replay (heuristic: window.online + 1s delay).
 * MVP — bulk promote: Workbox не передаёт URL→id mapping в default postMessage.
 * Phase 9+ — custom SW source для precise per-record updates.
 *
 * Returns count of promoted records.
 */
export async function bulkPromotePendingToSynced(): Promise<{
  projects: number;
  quotes: number;
}> {
  if (typeof indexedDB === 'undefined') {
    return { projects: 0, quotes: 0 };
  }
  const [pendingProjects, pendingQuotes] = await Promise.all([
    listProjectsBySyncStatus('pending'),
    listQuotesBySyncStatus('pending'),
  ]);
  for (const p of pendingProjects) {
    await updateProjectSyncStatus(p.id, 'synced');
  }
  for (const q of pendingQuotes) {
    await updateQuoteSyncStatus(q.id, 'synced');
  }
  return { projects: pendingProjects.length, quotes: pendingQuotes.length };
}

/**
 * Manual sync trigger from UI retry button.
 *
 * MVP behavior: dispatches window.online event — Workbox SW BackgroundSync
 * plugin listens для replay queue. Built-in Workbox exponential backoff handles retries.
 *
 * Returns true if triggered, false if offline (cannot sync).
 */
export function triggerManualSync(): boolean {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return false;
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('online'));
  }
  return true;
}
